import React, { memo, useCallback, useState, useMemo, useRef, useReducer, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { SectionList, SectionFilter, FiltersProvider } from '@Sage/ssm-web5';
import { Button, Spacer, Icon, useTheme } from '@Sage/hoi-poi-ui';
import { default as dateFormat } from 'date-fns/format';

import {
    getAutomationsLog,
    getAutomationLog,
    getAutomationsLogCount,
} from 'services/AutomationsService';

import Context from 'managers/Context';

import * as ENTITIES from 'constants/Entities';
import { FuzzyMap } from 'utils/fuzzy';
import { FuzzyActions } from 'actions';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { errorToast } from 'utils/toast';
import { getUserSfmUrl } from 'utils/getUrl';
import { getSrcUserCircleAvatar } from 'utils/getSrcAvatar';
import { getNumberFilters, cleanFilters } from 'utils/filters';
import { isEqual, isEmptyObject } from 'utils/objects';
import { logEvent } from 'utils/tracking';

import { AUTOMATION_TRIGGERS, AUTOMATION_ENTITIES } from './utils';
import AutomationsLogsEmptyView from './components/AutomationsLogsEmptyView';
import { BadgeCell, TextPopoverCell, IconCell } from 'components/TableCells';
import TagCell from './components/cells/TagCell';
import DetailLogModal from './components/DetailLogModal';
import AutomationsCreateMenu from './components/AutomationsCreateMenu';

import './styles.scss';

const initialState = {
    isFiltersOpen: false,
    numberFilters: 1,
    haveFilters: false,
    filters: null,
    selected: {},
};

function reducer(state, action) {
    switch (action.type) {
        case 'setIsFiltersOpen':
            return {
                ...state,
                isFiltersOpen: action.value,
            };
        case 'setFilters':
            return {
                ...state,
                filters: action.filters,
                numberFilters: action.numberFilters,
                haveFilters: action.haveFilters,
            };
        default:
            throw new Error('No action provided');
    }
}

const AutomationsLog = memo(
    ({
        openBuilder,
        goToTemplates,
        logId,
        showAutomationName = true,
        canCreate = true,
        showFiltersSidebar = true,
    }) => {
        const theme = useTheme();
        const dispatchStore = useDispatch();
        const [state, dispatch] = useReducer(reducer, { ...initialState });
        const [error, setError] = useState(false);
        const [quickFiltersKeys, setQuickFiltersKeys] = useState(
            showAutomationName
                ? ['runId', 'date', 'automation', 'level', 'triggerUser', 'entity', 'action']
                : ['runId', 'date', 'level', 'triggerUser', 'entity', 'action'],
        );
        const totalList = useRef([]);
        const tableRef = useRef(null);
        const filters = useRef({});
        const detailLogModalRef = useRef(null);

        useEffect(() => {
            logEvent({
                event: 'settings',
                submodule: 'automationsHistory',
                functionality: 'listView',
            });
        }, []);

        const getRowNodeId = useCallback((data) => String(data.id), []);

        const refresh = useCallback(() => {
            totalList.current = [];
            tableRef.current?.refresh({ resetOffset: true });
        }, []);

        const get = useCallback(
            (pageSize, startRow) => {
                if (startRow > totalList.current.length) {
                    return Promise.reject();
                }

                return getAutomationsLog({
                    count: pageSize,
                    offset: startRow,
                    ...filters.current,
                    idScene: logId,
                })
                    .then((data) => {
                        // Result processing
                        function resultProcessing(d) {
                            let result = {};
                            switch (d?.result) {
                                case 'success':
                                    result = {
                                        text: getLiteral('label_automation_result_success'),
                                        type: 'semanticPositive',
                                    };
                                    break;
                                case 'filtered':
                                    result = {
                                        text: getLiteral('label_automation_result_filtered'),
                                        type: 'default',
                                    };
                                    break;
                                case 'error':
                                    result = {
                                        text: getLiteral('label_automation_result_error'),
                                        type: 'semanticNegative',
                                    };
                                    break;
                                default:
                                    break;
                            }

                            return { result };
                        }

                        const entities = data.map((d) => {
                            const { src, placeholder } = getSrcUserCircleAvatar(d?.triggerUser?.id);

                            const entity = Object.values(ENTITIES).find(
                                (e) => e.trueName === d.entity,
                            );
                            let name = getLiteral(d?.triggerEvent?.action?.title);
                            if (d?.triggerEvent?.action?.canCreate) {
                                name += ` (${getLiteral('label_automation_event_creation_included')})`;
                            }

                            return {
                                ...d,
                                automation: d?.automation?.description,
                                automationId: d?.automation?.id,
                                blocked: d?.automation?.blocked,
                                ...resultProcessing(d),
                                triggeringUser: d?.triggerUser?.description,
                                triggeringUserId: d?.triggerUser?.id,
                                triggeringUserSrc: src,
                                triggeringUserPlaceholder: placeholder,
                                triggerEventIcon: d?.triggerEvent?.action?.icon,
                                triggerEvent: name,
                                triggerEntityIcon: d.entity,
                                triggerEntity: entity?.labels?.singular
                                    ? getLiteral(entity?.labels?.singular)
                                    : d.entity,
                                triggeringRecord:
                                    d?.triggerRecord?.description || getLiteral('label_no_data'),
                                triggeringRecordURL: entity?.route
                                    ? `/#${entity?.route}/${d?.triggerRecord?.id}`
                                    : '',
                            };
                        });

                        totalList.current.push(...entities);

                        return {
                            entities,
                        };
                    })
                    .catch((e) => {
                        console.error(e);
                        setError(true);
                        errorToast({
                            title: getLiteral('error_error'),
                            text: getLiteral('error_generalerror'),
                        });
                    });
            },
            [logId],
        );

        const getTotal = useCallback(() => {
            return getAutomationsLogCount({ ...filters.current, idScene: logId })
                .then((data) => {
                    return data?.total || 0;
                })
                .catch((e) => {
                    console.error(e);
                    setError(true);
                    errorToast({
                        title: getLiteral('error_error'),
                        text: getLiteral('error_generalerror'),
                    });
                });
        }, [logId]);

        const showLog = useCallback((row) => {
            getAutomationLog(row.id)
                .then((data) => {
                    detailLogModalRef.current?.open(row, data);
                    logEvent({
                        event: 'settings',
                        submodule: 'automationsHistory',
                        functionality: 'resultDetails',
                    });
                })
                .catch((e) => {
                    console.error(e);
                    errorToast({
                        title: getLiteral('error_error'),
                        text: getLiteral('error_generalerror'),
                    });
                });
        }, []);

        const tableProps = useMemo(
            () => ({
                components: {
                    BadgeCell,
                    TextPopoverCell,
                    IconCell,
                    TagCell,
                },
                columnDefs: [
                    {
                        colId: 'id',
                        field: 'id',
                        headerName: getLiteral('label_run_id'),
                        headerComponent: 'headerTextCell',
                        headerComponentParams: {
                            showTotal: true,
                        },
                        cellRenderer: 'textCell',
                        cellRendererParams: {
                            bold: true,
                            onClick: (row) => showLog(row.data),
                            hoverLink: true,
                        },
                        minWidth: 100,
                        width: 200,
                        suppressSizeToFit: true,
                        pinned: 'left',
                    },
                    {
                        colId: 'date',
                        field: 'date',
                        headerName: getLiteral('cfm_label_execution_date'),
                        cellRenderer: 'dateCell',
                        cellRendererParams: {
                            locale: () => Context?.config?.userData?.locale,
                            inputFormat: "yyyy-MM-dd'T'HH:mm:ss",
                            outputFormat: 'P p',
                        },
                        minWidth: 100,
                        width: 200,
                    },
                    {
                        colId: 'automation',
                        field: 'automation',
                        headerName: getLiteral('label_automation'),
                        cellRenderer: 'TagCell',
                        cellRendererParams: {
                            reload: get,
                            open: (id) => openBuilder(id),
                        },
                        minWidth: 100,
                        width: 500,
                    },
                    {
                        colId: 'result',
                        field: 'result',
                        headerName: getLiteral('cfm_label_result'),
                        cellRenderer: 'BadgeCell',
                        minWidth: 100,
                        width: 250,
                    },
                    {
                        colId: 'triggeringUser',
                        field: 'triggeringUser',
                        headerName: getLiteral('label_triggering_user'),
                        cellRenderer: 'avatarCell',
                        cellRendererParams: {
                            getUrl: (data) => {
                                const id = data.triggeringUserId;
                                if (!id) return;
                                return getUserSfmUrl(id) || '';
                            },
                            otherFields: {
                                src: 'triggeringUserSrc',
                                placeholder: 'triggeringUserPlaceholder',
                            },
                        },
                        minWidth: 200,
                    },
                    {
                        colId: 'triggerEntity',
                        field: 'triggerEntity',
                        headerName: getLiteral('label_triggering_entity'),
                        cellRenderer: 'IconCell',
                        cellRendererParams: {
                            iconValue: 'triggerEntityIcon',
                        },
                        minWidth: 200,
                    },
                    {
                        colId: 'triggerEvent',
                        field: 'triggerEvent',
                        headerName: getLiteral('label_triggering_event'),
                        cellRenderer: 'IconCell',
                        cellRendererParams: {
                            iconValue: 'triggerEventIcon',
                        },
                        minWidth: 250,
                    },
                    {
                        colId: 'triggeringRecord',
                        field: 'triggeringRecord',
                        headerName: getLiteral('label_triggering_record'),
                        cellRenderer: 'avatarCell',
                        cellRendererParams: {
                            getUrl: (data) => {
                                return data.triggeringRecordURL || '';
                            },
                        },
                        minWidth: 250,
                    },
                ].filter((col) => (showAutomationName ? true : col.colId !== 'automation')),
                forceActionsRefresh: true,
                actions: [
                    (row) => {
                        return {
                            icon: (
                                <Icon
                                    name="document"
                                    color={theme.colors.actionMinor[600]}
                                    onClick={() => showLog(row)}
                                />
                            ),
                            tooltip: { content: getLiteral('action_see_more_result_details') },
                        };
                    },
                ],
                onRef: (ref) => {
                    tableRef.current = ref;
                },
            }),
            [get, showLog, openBuilder, showAutomationName, theme.colors.actionMinor],
        );

        const getErrorView = useCallback(() => {
            return {
                title: getLiteral('error_an_error_occurred'),
                subtitle: getLiteral('error_reload_table'),
                button: (
                    <Button type="secondary" onClick={refresh}>
                        {getLiteral('action_reload_table')}
                    </Button>
                ),
            };
        }, [refresh]);

        const onChangeFilters = useCallback(
            (values) => {
                const numberFilters = getNumberFilters(values);
                dispatch({
                    type: 'setFilters',
                    numberFilters,
                    haveFilters: numberFilters > 0,
                    filters: values,
                });

                let dateFrom, dateTo;

                if (values?.date?.[0]) {
                    dateFrom = dateFormat(values?.date?.[0], "yyyy-MM-dd'T'00:00:00.000'Z'");
                }

                if (values?.date?.[1]) {
                    dateTo = dateFormat(values?.date?.[1], "yyyy-MM-dd'T'23:59:59.000'Z'");
                }

                const newFilters = cleanFilters({
                    dateFrom,
                    dateTo,
                    automation: values?.automation,
                    level: values?.level?.value,
                    triggerUser: values?.triggerUser?.value,
                    entity: values?.entity?.value,
                    action: values?.action?.value,
                    entityId: parseInt(values?.entityId?.value, 10),
                    id: values?.runId
                        ? parseInt(values?.runId?.toString().replace(/[^0-9]/g, ''), 10) ||
                          undefined
                        : undefined,
                });

                if (
                    isEqual(newFilters, filters.current) ||
                    (isEmptyObject(values) && isEmptyObject(filters.current))
                ) {
                    filters.current = newFilters;
                } else {
                    filters.current = newFilters;
                    refresh();
                }

                logEvent({
                    event: 'settings',
                    submodule: 'automationsHistory',
                    functionality: 'filter',
                });
            },
            [refresh],
        );

        const openFilters = useCallback(() => {
            dispatch({
                type: 'setIsFiltersOpen',
                value: true,
            });
        }, []);

        const onCloseFilters = useCallback(
            () =>
                dispatch({
                    type: 'setIsFiltersOpen',
                    value: false,
                }),
            [],
        );

        const toggleQuickFilter = useCallback(
            (id) => {
                const alreadyExists = quickFiltersKeys.includes(id);
                const newQuickFilters = alreadyExists
                    ? quickFiltersKeys.filter((i) => i !== id)
                    : [...quickFiltersKeys, id];

                setQuickFiltersKeys(newQuickFilters);
            },
            [quickFiltersKeys],
        );

        const onReOrder = useCallback(
            (startIndex, endIndex) => {
                const result = [...quickFiltersKeys];
                const [removed] = result.splice(startIndex, 1);
                result.splice(endIndex, 0, removed);
                setQuickFiltersKeys(result);
            },
            [quickFiltersKeys],
        );

        const filterSchema = useMemo(() => {
            const showTriggeringEntityId = state.filters?.entity?.value;
            const schema = [
                {
                    label: getLiteral('action_search_run_id'),
                    id: 'runId',
                    fields: [
                        {
                            id: 'runId',
                            dataType: 'text',
                            inputProps: {
                                placeholder: getLiteral('cfm_placeholder_search'),
                                overrides: {
                                    input: {
                                        onKeyPress: (e) => {
                                            const charCode =
                                                typeof e.which == 'undefined' ? e.keyCode : e.which;
                                            const charStr = String.fromCharCode(charCode);
                                            if (!charStr.match(/^[0-9]+$/)) e.preventDefault();
                                        },
                                    },
                                },
                            },
                        },
                    ],
                },
                {
                    label: getLiteral('cfm_label_execution_date'),
                    id: 'date',
                    fields: [
                        {
                            id: 'date',
                            dataType: 'date',
                        },
                    ],
                },
                {
                    label: getLiteral('label_automation'),
                    id: 'automation',
                    fields: [
                        {
                            id: 'automation',
                            dataType: 'text',
                            inputProps: {
                                placeholder: getLiteral('cfm_placeholder_search'),
                            },
                        },
                    ],
                },
                {
                    label: getLiteral('cfm_label_result'),
                    id: 'level',
                    fields: [
                        {
                            id: 'level',
                            dataType: 'singleValueList',
                            inputProps: {
                                placeholder: getLiteral('cfm_placeholder_search'),
                                options: [
                                    {
                                        label: getLiteral('label_automation_result_success'),
                                        value: 2,
                                    },
                                    {
                                        label: getLiteral('label_automation_result_filtered'),
                                        value: 0,
                                    },
                                    {
                                        label: getLiteral('label_automation_result_error'),
                                        value: -1,
                                    },
                                ],
                            },
                        },
                    ],
                },
                {
                    label: getLiteral('label_triggering_user'),
                    id: 'triggerUser',
                    fields: [
                        {
                            id: 'triggerUser',
                            dataType: 'singleValueList',
                            inputProps: {
                                placeholder: getLiteral('cfm_placeholder_search'),
                                isFuzzy: true,
                                defaultOptions: true,
                                loadOptions: (text) =>
                                    dispatchStore(
                                        FuzzyActions.getFuzzy({
                                            list: 'usuarios',
                                            field: '',
                                            text,
                                        }),
                                    ),
                            },
                        },
                    ],
                },
                {
                    label: getLiteral('label_triggering_event'),
                    id: 'action',
                    fields: [
                        {
                            id: 'action',
                            dataType: 'singleValueList',
                            inputProps: {
                                placeholder: getLiteral('cfm_placeholder_search'),
                                options: Object.entries(AUTOMATION_TRIGGERS).map(
                                    ([key, value]) => ({
                                        iconType: value.icon,
                                        value: key,
                                        label: getLiteral(value.name),
                                    }),
                                ),
                            },
                        },
                    ],
                },
                {
                    label: getLiteral('label_triggering_entity'),
                    id: 'entity',
                    fields: [
                        {
                            id: 'entity',
                            dataType: 'singleValueList',
                            inputProps: {
                                placeholder: getLiteral('cfm_placeholder_search'),
                                options: AUTOMATION_ENTITIES.map((entity) => ({
                                    label: getLiteral(entity.labels.singular),
                                    value: entity.trueName,
                                    iconType:
                                        entity.trueName === 'salesorders'
                                            ? 'salesOrders'
                                            : entity.trueName,
                                })),
                            },
                        },
                    ],
                    subfilterSchema: showTriggeringEntityId
                        ? [
                              {
                                  id: 'entityId',
                                  isPinnable: false,
                                  isRemovable: false,
                                  label: getLiteral('label_triggering_record'),
                                  fields: [
                                      {
                                          id: 'entityId',
                                          dataType: 'singleValueList',
                                          isSubFilter: true,
                                          inputProps: {
                                              placeholder: getLiteral('cfm_placeholder_search'),
                                              isFuzzy: true,
                                              defaultOptions: false,
                                              loadOptions: (text) => {
                                                  let listName = '';
                                                  switch (filters.current?.entity) {
                                                      case 'accounts':
                                                          listName = 'empresas';
                                                          break;
                                                      case 'contacts':
                                                          listName = 'contactos';
                                                          break;
                                                      case 'opportunities':
                                                          listName = 'expedientes';
                                                          break;
                                                      case 'activities':
                                                          listName = 'actividades';
                                                          break;
                                                      case 'salesorders':
                                                          listName = 'z_tblpedidos';
                                                          break;
                                                      case 'users':
                                                          listName = 'users';
                                                          break;
                                                      default:
                                                          listName = '';
                                                  }

                                                  if (!listName) return Promise.resolve([]);

                                                  return dispatchStore(
                                                      FuzzyActions.getFuzzy({
                                                          text,
                                                          ...FuzzyMap[listName],
                                                      }),
                                                  );
                                              },
                                          },
                                      },
                                  ],
                              },
                          ]
                        : [],
                },
            ].filter((filter) => (showAutomationName ? true : filter.id !== 'automation'));
            return schema;
        }, [dispatchStore, showAutomationName, state.filters?.entity?.value]);

        const quickFilterSchema = useMemo(() => {
            return filterSchema.filter((filter) => quickFiltersKeys.includes(filter.id));
        }, [filterSchema, quickFiltersKeys]);

        const generalFilterSchema = useMemo(() => {
            return filterSchema.filter((filter) => !quickFiltersKeys.includes(filter.id));
        }, [filterSchema, quickFiltersKeys]);

        const defaultOpenFilters = useMemo(
            () => ({
                entity: {
                    open: false,
                    subfilters: {
                        entityId: true,
                    },
                },
            }),
            [],
        );

        const haveFilters = state.haveFilters;

        return (
            <FiltersProvider>
                <div className="fm-automations-settings__toolbar">
                    <SectionFilter
                        useQuickFilters
                        quickFilters={quickFiltersKeys}
                        schema={filterSchema}
                        onChange={onChangeFilters}
                        values={state.filters}
                        noFiltersProvider={true}
                        defaultOpenFilters={defaultOpenFilters}
                    />
                    {showFiltersSidebar && (
                        <>
                            {!haveFilters && (
                                <Button
                                    type="terciary"
                                    size="medium"
                                    icon="filterList"
                                    onClick={openFilters}
                                >
                                    {getLiteral('action_filters')}
                                </Button>
                            )}
                            {haveFilters && (
                                <Button
                                    type="secondary"
                                    size="medium"
                                    icon="filterListActive"
                                    onClick={openFilters}
                                >
                                    {getLiteralWithParameters('label_filters_button', [
                                        state.numberFilters,
                                    ])}
                                </Button>
                            )}
                        </>
                    )}
                    {canCreate && (
                        <AutomationsCreateMenu
                            openBuilder={openBuilder}
                            goToTemplates={goToTemplates}
                        />
                    )}
                </div>
                <Spacer y={2} />
                <SectionList
                    id="automations-log"
                    className="automations-list"
                    {...tableProps}
                    useLoaderPlaceholder
                    hasError={error}
                    getRowNodeId={getRowNodeId}
                    errorViewProps={getErrorView}
                    customEmptyViewComponent={AutomationsLogsEmptyView}
                    //Infinite scrolling props
                    forceActionsRefresh
                    initTable={get}
                    getTotal={getTotal}
                    pageSize={50}
                    useLazyLoad
                    initOnReady
                />
                {showFiltersSidebar && (
                    <SectionFilter
                        isOpen={state.isFiltersOpen}
                        onClose={onCloseFilters}
                        id="fm-automations-log__filter-section"
                        quickSchema={quickFilterSchema}
                        schema={generalFilterSchema}
                        onChange={onChangeFilters}
                        values={state.filters}
                        toggleQuickFilter={toggleQuickFilter}
                        onReOrder={onReOrder}
                        isPinnable={true}
                        noFiltersProvider={true}
                        defaultOpenFilters={defaultOpenFilters}
                    />
                )}
                <DetailLogModal onRef={(ref) => (detailLogModalRef.current = ref)} />
            </FiltersProvider>
        );
    },
);

export default AutomationsLog;
